import './Home.scss'
import type { ReactNode } from 'react'
import { Clients } from '#src/components/Clients.js'
import { Grid } from '#src/components/Grid.js'
import { EagerDatoImage } from '#src/components/image/EagerDatoImage.js'
import { LazyLoad } from '#src/components/LazyLoad.js'
import { LocalizedLink } from '#src/components/LocalizedLink.js'
import { StructuredData } from '#src/components/StructuredData.js'
import type { HomeData } from '#src/data/data.js'
import { useIsChildRoute } from '#src/hooks/useIsChildRoute.js'

interface HomeProps {
  data: HomeData
  clients: string[]
  children?: ReactNode
}

/** Shared with the route's meta, which preloads the hero at the same sizes. */
export const HERO_SIZES = ['(max-width: 992px) 100vw', '80vw']

export function Home(props: HomeProps): ReactNode {
  const { heroImage, heading, grid, structuredData } = props.data
  const popupIsOpen = useIsChildRoute('/')

  return (
    <>
      <section className="intro" aria-hidden={popupIsOpen}>
        <div className="intro__inner-container">
          <div className="intro__text">
            <h1 className="intro__heading">{heading}</h1>
          </div>
          <div className="intro__image-container">
            <EagerDatoImage
              className="intro__image"
              responsiveImage={heroImage.responsiveImage}
              sizes={HERO_SIZES}
            />
          </div>
        </div>
      </section>

      {grid && <Grid grid={grid} galleryPath="/gallery/" leftRight={true} />}

      <LazyLoad>
        <section className="info">
          <div className="info__container">
            {props.data.selectedCases.map(selectedCase => (
              <LocalizedLink
                href={`/selected-cases/${selectedCase.slug}`}
                className="info__column"
                key={selectedCase.slug}
                style={{
                  backgroundImage: `url(${selectedCase.coverImage.responsiveImage.src})`, // todo: make me responsive, please!
                }}
              >
                <div className="info__text-container">
                  <p className="info__subtitle">selected cases</p>
                  <h4 className="info__title">{selectedCase.name}</h4>
                  <p className="info__more">read more</p>
                </div>
              </LocalizedLink>
            ))}
          </div>
        </section>
      </LazyLoad>

      <Clients clients={props.clients} />

      {props.children}

      {structuredData && <StructuredData data={structuredData} />}
    </>
  )
}
